import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Notification, NotificationState, initialNotificationState } from './state'

/**
 * Live march tickers ("Homecoming in 3:08") live in this store only because the war ticker reads
 * it. They are not mail and must not drive the unread badge: they are born unread, the mailbox
 * hides them, so every one of them left a badge the player could never clear.
 */
const EPHEMERAL_TICKER_KINDS = new Set(['mvm-incoming', 'mvm-returning'])
const isEphemeralTicker = (n: Notification): boolean => {
  const kind = (n?.metadata as Record<string, unknown> | null | undefined)?.kind
  return typeof kind === 'string' && EPHEMERAL_TICKER_KINDS.has(kind)
}
const countsAsUnread = (n: Notification): boolean => !n.read && !isEphemeralTicker(n)

export const notificationSlice = createSlice({
  name: 'notifications',
  initialState: initialNotificationState as NotificationState,
  reducers: {
    setNotifications: (state: NotificationState, action: PayloadAction<Notification[]>) => {
      // Normalize notifications from backend
      const serverNotifs = action.payload.map((n) => ({
        ...n,
        id: n.id || n._id || '',
        read: n.read ?? n.isRead ?? false,
        type: n.type || 'system',
        timestamp: n.timestamp || (n.createdAt ? new Date(n.createdAt).getTime() : Date.now()),
      }))

      // Build a set of IDs the server returned so we can detect client-only ones
      const serverIds = new Set(serverNotifs.map(n => n.id).filter(Boolean))

      // Preserve client-only notifications (added via addNotification but not yet on the
      // server) that are less than 24 hours old. These cover real-time socket events such
      // as prisoner-action and ransom responses that the server may not persist immediately.
      const DAY_MS = 24 * 60 * 60 * 1000
      const now = Date.now()
      const preserved = state.notifications.filter(n => {
        const id = n.id || n._id || ''
        return id && !serverIds.has(id) && (now - (n.timestamp || 0)) < DAY_MS
      })

      // Merge: server data is authoritative for IDs it owns; client-only ones are appended
      const merged = [...serverNotifs, ...preserved]
        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
        .slice(0, 50)

      state.notifications = merged
      state.unreadCount = merged.filter(countsAsUnread).length

      // Calculate unread counts by type
      state.unreadCounts = {
        messages: merged.filter(n => n.type === 'messages' && countsAsUnread(n)).length,
        war: merged.filter(n => n.type === 'war' && countsAsUnread(n)).length,
        alliance: merged.filter(n => n.type === 'alliance' && countsAsUnread(n)).length,
        system: merged.filter(n => n.type === 'system' && countsAsUnread(n)).length,
        reports: merged.filter(n => n.type === 'reports' && countsAsUnread(n)).length,
        diplomacy: merged.filter(n => n.type === 'diplomacy' && countsAsUnread(n)).length,
      }
    },
    addNotification: (state: NotificationState, action: PayloadAction<Notification>) => {
      // Normalize notification data
      const notification = {
        ...action.payload,
        id: action.payload.id || action.payload._id || '',
        _id: action.payload._id || action.payload.id || '',
        read: action.payload.read ?? action.payload.isRead ?? false,
        isRead: action.payload.isRead ?? action.payload.read ?? false,
        type: action.payload.type || 'system',
        timestamp: action.payload.timestamp || (action.payload.createdAt ? new Date(action.payload.createdAt).getTime() : Date.now()),
      }
      
      // Check if notification already exists
      const existingIndex = state.notifications.findIndex(n => 
        (n.id && notification.id && n.id === notification.id) ||
        (n._id && notification._id && n._id === notification._id)
      )
      
      if (existingIndex !== -1) {
        // Update existing notification
        const existingNotification = state.notifications[existingIndex]
        const wasUnread = !existingNotification.read
        const isNowUnread = !notification.read
        
        state.notifications[existingIndex] = notification
        
        // Update unread count
        if (wasUnread && !isNowUnread) {
          state.unreadCount = Math.max(0, state.unreadCount - 1)
          // Update unreadCounts by type
          if (state.unreadCounts && notification.type) {
            const type = notification.type as keyof typeof state.unreadCounts
            if (state.unreadCounts[type] !== undefined) {
              state.unreadCounts[type] = Math.max(0, state.unreadCounts[type] - 1)
            }
          }
        } else if (!wasUnread && isNowUnread) {
          state.unreadCount += 1
          // Update unreadCounts by type
          if (state.unreadCounts && notification.type) {
            const type = notification.type as keyof typeof state.unreadCounts
            if (state.unreadCounts[type] !== undefined) {
              state.unreadCounts[type] = (state.unreadCounts[type] || 0) + 1
            }
          }
        }
      } else {
        // Add new notification
        state.notifications.unshift(notification) // Add to beginning
        if (countsAsUnread(notification)) {
          state.unreadCount += 1
          // Update unreadCounts by type
          if (state.unreadCounts && notification.type) {
            const type = notification.type as keyof typeof state.unreadCounts
            if (state.unreadCounts[type] !== undefined) {
              state.unreadCounts[type] = (state.unreadCounts[type] || 0) + 1
            }
          }
        }
        // Keep only last 50 notifications for performance
        if (state.notifications.length > 50) {
          const removed = state.notifications.pop()
          if (removed && !removed.read) {
            state.unreadCount = Math.max(0, state.unreadCount - 1)
          }
        }
      }
    },
    markAsRead: (state: NotificationState, action: PayloadAction<string>) => {
      const notification = state.notifications.find(n => 
        n.id === action.payload || n._id === action.payload
      )
      if (notification && !notification.read) {
        notification.read = true
        notification.isRead = true
        state.unreadCount = Math.max(0, state.unreadCount - 1)
        // Update unreadCounts by type
        if (state.unreadCounts && notification.type) {
          const type = notification.type as keyof typeof state.unreadCounts
          if (state.unreadCounts[type] !== undefined) {
            state.unreadCounts[type] = Math.max(0, state.unreadCounts[type] - 1)
          }
        }
      }
    },
    markAllAsRead: (state: NotificationState) => {
      state.notifications.forEach(n => {
        n.read = true
        n.isRead = true
      })
      state.unreadCount = 0
      if (state.unreadCounts) {
        state.unreadCounts = {
          messages: 0,
          war: 0,
          alliance: 0,
          system: 0,
          reports: 0,
          diplomacy: 0,
        }
      }
    },
    removeNotification: (state: NotificationState, action: PayloadAction<string>) => {
      const index = state.notifications.findIndex(n => 
        n.id === action.payload || n._id === action.payload
      )
      if (index !== -1) {
        const notification = state.notifications[index]
        if (!notification.read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1)
          // Update unreadCounts by type
          if (state.unreadCounts && notification.type) {
            const type = notification.type as keyof typeof state.unreadCounts
            if (state.unreadCounts[type] !== undefined) {
              state.unreadCounts[type] = Math.max(0, state.unreadCounts[type] - 1)
            }
          }
        }
        state.notifications.splice(index, 1)
      }
    },
    clearNotifications: (state: NotificationState) => {
      state.notifications = []
      state.unreadCount = 0
      if (state.unreadCounts) {
        state.unreadCounts = {
          messages: 0,
          war: 0,
          alliance: 0,
          system: 0,
          reports: 0,
          diplomacy: 0,
        }
      }
    },
    incrementAllianceManageChatUnread: (state: NotificationState) => {
      state.allianceManageChatUnread = (state.allianceManageChatUnread ?? 0) + 1
    },
    resetAllianceManageChatUnread: (state: NotificationState) => {
      state.allianceManageChatUnread = 0
    },
  }
})

export const {
  addNotification,
  setNotifications,
  markAsRead,
  markAllAsRead,
  removeNotification,
  clearNotifications,
  incrementAllianceManageChatUnread,
  resetAllianceManageChatUnread,
} = notificationSlice.actions

export default notificationSlice.reducer

